import { Socket, Channel } from 'phoenix';
import { useState, useEffect, useRef } from 'react';
import { getUserAuth } from '../core/_axios';
import { BASE_WSS_URL } from '../core/_requests';
import { useAuth } from '../AuthContext';

interface ReceivedEmail {
  id: string;
  from: string;
  to: string;
  subject: string;
  status: string;
  received_at: string;
  attachment_count: number;
  thread_id?: string;
  is_reply?: boolean;
}

interface UseReceivedEmailsWebSocketProps {
  onEmailCreated?: (email: ReceivedEmail) => void;
  onEmailUpdated?: (email: ReceivedEmail) => void;
  onUnreadCountChanged?: (count: number) => void;
}

// Share the same socket instance with email campaigns
let sharedSocket: Socket | null = null;
let sharedChannel: Channel | null = null;
let connectionCount = 0;

export default function useReceivedEmailsWebSocket({
  onEmailCreated,
  onEmailUpdated,
  onUnreadCountChanged
}: UseReceivedEmailsWebSocketProps = {}) {
  const { currentUserAccountUniqueId, viewingAllAccounts } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Use refs to store the latest callbacks without triggering re-renders
  const onEmailCreatedRef = useRef(onEmailCreated);
  const onEmailUpdatedRef = useRef(onEmailUpdated);
  const onUnreadCountChangedRef = useRef(onUnreadCountChanged);
  
  // Update refs when callbacks change
  useEffect(() => {
    onEmailCreatedRef.current = onEmailCreated;
  }, [onEmailCreated]);
  
  useEffect(() => {
    onEmailUpdatedRef.current = onEmailUpdated;
  }, [onEmailUpdated]);
  
  useEffect(() => {
    onUnreadCountChangedRef.current = onUnreadCountChanged;
  }, [onUnreadCountChanged]);

  useEffect(() => {
    // When viewing all accounts, we don't have a specific account ID but still want to connect
    const channelToUse = viewingAllAccounts ? 'all_accounts' : currentUserAccountUniqueId;
    
    if (!channelToUse && !viewingAllAccounts) {
      return;
    }

    connectionCount++;
    const actualChannelName = viewingAllAccounts ? 'web:all_accounts' : `web:account:${channelToUse}`;
    console.log(`[ReceivedEmailsWebSocket] Component mounted, connection count: ${connectionCount}, channel: ${actualChannelName}`);

    // Create unique handler functions for this component instance
    const uniqueId = Math.random().toString(36).substr(2, 9);
    
    const emailCreatedHandler = (data: any) => {
      console.log(`[ReceivedEmailsWebSocket-${uniqueId}] Email created event:`, data);
      if (onEmailCreatedRef.current && data.email) {
        onEmailCreatedRef.current(data.email);
      }
      
      // Update unread count if the email is unread
      if (data.email && data.email.status === 'unread') {
        setUnreadCount(prev => {
          const newCount = prev + 1;
          if (onUnreadCountChangedRef.current) {
            onUnreadCountChangedRef.current(newCount);
          }
          return newCount;
        });
      }
    };
    
    const emailUpdatedHandler = (data: any) => {
      console.log(`[ReceivedEmailsWebSocket-${uniqueId}] Email updated event:`, data);
      if (onEmailUpdatedRef.current && data.email) {
        onEmailUpdatedRef.current(data.email);
      }
      
      // Update unread count if status changed
      if (data.email && data.previousStatus === 'unread' && data.email.status === 'read') {
        setUnreadCount(prev => {
          const newCount = Math.max(0, prev - 1);
          if (onUnreadCountChangedRef.current) {
            onUnreadCountChangedRef.current(newCount);
          }
          return newCount;
        });
      }
    };

    // If we already have a connection, just add listeners
    if (sharedSocket && sharedChannel) {
      console.log('[ReceivedEmailsWebSocket] Reusing existing WebSocket connection');
      
      sharedChannel.on('received_email_created', emailCreatedHandler);
      sharedChannel.on('received_email_updated', emailUpdatedHandler);
      
      setIsConnected(true);
      
      // Cleanup function
      return () => {
        connectionCount--;
        console.log(`[ReceivedEmailsWebSocket] Component unmounted, connection count: ${connectionCount}`);
        
        // Only disconnect if this was the last component using the connection
        if (connectionCount === 0 && sharedChannel && sharedSocket) {
          console.log('[ReceivedEmailsWebSocket] Last component unmounted, closing WebSocket connection');
          sharedChannel.leave();
          sharedSocket.disconnect();
          sharedChannel = null;
          sharedSocket = null;
        }
      };
    }

    // Create new connection
    console.log('[ReceivedEmailsWebSocket] Creating new WebSocket connection');
    
    const socket = new Socket(`${BASE_WSS_URL}/socket/web`, {
      params: {
        web_token: getUserAuth()?.api_token
      }
    });

    const channelName = viewingAllAccounts ? 'web:all_accounts' : `web:account:${channelToUse}`;
    const accountChannel = socket.channel(channelName, {
      from_web: true
    });

    // Store references
    sharedSocket = socket;
    sharedChannel = accountChannel;

    // Set up event handlers
    accountChannel.on('received_email_created', emailCreatedHandler);
    accountChannel.on('received_email_updated', emailUpdatedHandler);
    
    socket.connect();
    
    accountChannel.join()
      .receive("ok", resp => {
        if (resp.connection_error) {
          console.error("[ReceivedEmailsWebSocket] Connection error", resp.connection_error);
          setIsConnected(false);
          return;
        }
        setIsConnected(true);
        console.log("[ReceivedEmailsWebSocket] Successfully joined account channel");
      })
      .receive("error", resp => {
        console.error("[ReceivedEmailsWebSocket] Failed to join channel", resp);
        setIsConnected(false);
      });

    return () => {
      connectionCount--;
      console.log(`[ReceivedEmailsWebSocket] Component unmounted, connection count: ${connectionCount}`);
      
      // Only disconnect if this was the last component using the connection
      if (connectionCount === 0) {
        console.log('[ReceivedEmailsWebSocket] Last component unmounted, closing WebSocket connection');
        accountChannel.leave();
        socket.disconnect();
        sharedChannel = null;
        sharedSocket = null;
      }
    };
  }, [currentUserAccountUniqueId, viewingAllAccounts]); // Re-run when account or viewing mode changes

  return {
    isConnected,
    unreadCount
  };
}